/**
 * Solicitudes de alta de empresa (firm_admin).
 *
 * El estudio NO crea tenants (Opción A): solicita a Zenlatam crear una empresa
 * nueva o vincular una existente. Aquí se listan las solicitudes y se crean.
 */
import { useState } from 'react'
import { Inbox, PlusCircle } from 'lucide-react'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { type BadgeConfig, ConfigBadge } from '@/components/Badge'
import { Button } from '@/components/Button'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { ModalCancelButton } from '@/components/ModalActions'
import { Modal } from '@/components/ds/Modal'
import { DataTable } from '@/components/ds/DataTable'
import { ToastContainer, useToast } from '@/components/Toast'
import { getApiErrorMessage } from '@/lib/apiClient'
import {
  useFirmRequests, useCreateClientRequest, type FirmClientRequest,
} from '@/modules/firm/hooks/useFirm'
import { formatDate } from './firmShared'

const fieldCls =
  'w-full px-3 py-2 text-sm rounded-lg bg-surface border border-divider text-content focus:outline-none focus:ring-2 focus:ring-focus-ring'

const KIND_CFG: BadgeConfig<'new' | 'link'> = {
  new: { label: 'Nueva empresa', variant: 'blue' },
  link: { label: 'Vincular existente', variant: 'purple' },
}

const STATUS_CFG: BadgeConfig<FirmClientRequest['status']> = {
  pending: { label: 'Pendiente', variant: 'amber' },
  approved: { label: 'Aprobada', variant: 'emerald' },
  rejected: { label: 'Rechazada', variant: 'red' },
}

export function FirmRequestsPage() {
  const { data: requests, isLoading } = useFirmRequests()
  const createReq = useCreateClientRequest()
  const toast = useToast()

  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<'new' | 'link'>('new')
  const [companyName, setCompanyName] = useState('')
  const [taxId, setTaxId] = useState('')
  const [existingSchema, setExistingSchema] = useState('')
  const [notes, setNotes] = useState('')

  const reset = () => {
    setKind('new'); setCompanyName(''); setTaxId(''); setExistingSchema(''); setNotes('')
  }

  const canSubmit =
    companyName.trim().length > 0 &&
    (kind === 'new' || existingSchema.trim().length > 0) &&
    !createReq.isPending

  const submit = async () => {
    if (!canSubmit) return
    try {
      await createReq.mutateAsync({
        kind,
        company_name: companyName.trim(),
        tax_id: taxId.trim() || undefined,
        existing_tenant_schema: kind === 'link' ? existingSchema.trim() : undefined,
        notes: notes.trim() || undefined,
      })
      toast.success('Solicitud enviada a Zenlatam.')
      reset()
      setOpen(false)
    } catch (e) {
      toast.error(getApiErrorMessage(e))
    }
  }

  return (
    <div className="p-4 sm:p-6">
      <PageHeader
        title="Solicitudes de empresa"
        subtitle="Pide a Zenlatam crear una empresa nueva o vincular una existente a tu estudio."
        help={HELP['firm.requests']}
      >
        <Button onClick={() => setOpen(true)} variant="primary">
          <PlusCircle size={15} /> Nueva solicitud
        </Button>
      </PageHeader>

      <DataTable
        rows={requests ?? []}
        rowKey={r => r.id}
        loading={isLoading}
        loadingNode={<TableSkeleton rows={4} cols={5} />}
        minWidth={820}
        caption="Solicitudes de alta de empresa"
        empty={
          <EmptyState icon={Inbox} title="Sin solicitudes"
            hint="Cuando se solicite una empresa, aparecerá aquí con su estado." />
        }
        columns={[
          {
            key: 'company_name', header: 'Empresa', sortable: true,
            render: r => (
              <>
                <span className="font-medium text-content">{r.company_name}</span>
                {r.tax_id && <span className="block text-xs text-content-subtle">{r.tax_id}</span>}
                {r.status === 'approved' && r.resolved_tenant_schema && (
                  <span className="block text-xs text-success-content-soft">
                    → {r.resolved_tenant_schema}
                  </span>
                )}
              </>
            ),
          },
          {
            key: 'kind', header: 'Tipo', sortable: true, width: 150,
            render: r => <ConfigBadge value={r.kind} config={KIND_CFG} />,
          },
          {
            key: 'status', header: 'Estado', sortable: true, width: 130,
            render: r => <ConfigBadge value={r.status} config={STATUS_CFG} />,
          },
          {
            key: 'requested_by', header: 'Solicitada por', sortable: true,
            sortValue: r => r.requested_by_detail?.full_name ?? null,
            render: r => (
              <span className="text-content-muted">
                {r.requested_by_detail?.full_name || <span className="text-content-subtle">—</span>}
              </span>
            ),
          },
          {
            key: 'created_at', header: 'Fecha', sortable: true, width: 130,
            // ⚠️ Se ordena por el ISO crudo: `formatDate` imprime dd-mm-aaaa,
            // que como texto pondria enero de 2020 antes que marzo de 2019.
            render: r => <span className="text-content-muted">{formatDate(r.created_at)}</span>,
          },
        ]}
      />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Solicitar empresa"
        size="sm"
        actions={
          <>
            <ModalCancelButton onClick={() => setOpen(false)} />
            <Button onClick={submit} disabled={!canSubmit} isPending={createReq.isPending} variant="primary" size="lg">
              Enviar solicitud
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="firmrequestspage-tipo-de-solicitud" className="block text-sm font-medium text-content mb-1">Tipo de solicitud</label>
            <select id="firmrequestspage-tipo-de-solicitud" value={kind} onChange={(e) => setKind(e.target.value as 'new' | 'link')} className={fieldCls}>
              <option value="new">Crear una empresa nueva</option>
              <option value="link">Vincular una empresa existente</option>
            </select>
          </div>
          <div>
            <label htmlFor="firmrequestspage-nombre-de-la-empresa" className="block text-sm font-medium text-content mb-1">Nombre de la empresa *</label>
            <input id="firmrequestspage-nombre-de-la-empresa" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={fieldCls}
              placeholder="Razón social" />
          </div>
          <div>
            <label htmlFor="firmrequestspage-rut-opcional" className="block text-sm font-medium text-content mb-1">RUT (opcional)</label>
            <input id="firmrequestspage-rut-opcional" value={taxId} onChange={(e) => setTaxId(e.target.value)} className={fieldCls}
              placeholder="76.123.456-7" />
          </div>
          {kind === 'link' && (
            <div>
              <label htmlFor="firmrequestspage-schema-del-tenant-existente" className="block text-sm font-medium text-content mb-1">Schema del tenant existente *</label>
              <input id="firmrequestspage-schema-del-tenant-existente" value={existingSchema} onChange={(e) => setExistingSchema(e.target.value)} className={fieldCls}
                placeholder="ej. comercial_andes" />
            </div>
          )}
          <div>
            <label htmlFor="firmrequestspage-notas-opcional" className="block text-sm font-medium text-content mb-1">Notas (opcional)</label>
            <textarea id="firmrequestspage-notas-opcional" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={fieldCls}
              placeholder="Información adicional para Zenlatam" />
          </div>
        </div>
      </Modal>

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}

export default FirmRequestsPage
